import type { SheetVariant } from '@sunwebgroup/uds-ui/sheet'
import type { CheckoutServiceParticipant } from '../../../domain/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Menu } from '@sunwebgroup/uds-ui/menu'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { TitleListCell } from '@sunwebgroup/uds-ui/title-list-cell'
import { ToggleButton, ToggleButtonGroup } from '@sunwebgroup/uds-ui/toggle-button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import { isEmpty, values } from 'ramda'
import * as React from 'react'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import {
  GENDERS_ORDER,
  NO_GENDER,
  NO_SIZE,
  SIZES_ORDER,
} from '@/scopes/checkout/components/services/ClothingService/constants'
import {
  clothingServiceIsBeingEdited,
  useClothingServiceStore,
} from '@/scopes/checkout/components/services/ClothingService/store'
import {
  saveEditingClothingServiceValue,
  updateEditingClothingServiceGender,
  updateEditingClothingServiceSize,
  updateEditingClothingServiceValue,
} from '@/scopes/checkout/domain/actions/services/clothing'
import { useClickFilterClothingService } from '@/scopes/checkout/domain/analytics/useClickFilterClothingService'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createClothingServiceEvent } from '../../../domain/analytics'
import { ServiceType } from '../../../domain/types'
import genericStyles from '../GenericService/GenericService.module.css'
import styles from './ClothingComponents.module.css'
import {
  clothingGenderSelector,
  clothingServiceEditingValueByIdSelector,
  clothingServiceOptionsListSelector,
  clothingSizeSelector,
} from './selectors'

export function SweaterService() {
  const intl = useIntl()
  const { service } = useService(ServiceType.Sweater)
  useSendAncillaryAvailable(service, 'selectSweater')

  if (!service)
    return null

  const { rooms, participants, options } = service

  if (isEmpty(participants))
    return null
  if (isEmpty(options))
    return null

  return (
    <CheckoutSection
      title={intl.formatMessage({ id: 'checkout_steps_clothing_sweater_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_clothing_sweater_description' })}
      glyph={getIconForService(ServiceType.Sweater)}
    >
      <div className={cn(styles.flexContent, styles.flexContentGap12)}>
        {rooms.map(({ id: roomId, name: roomName, participantIds }) => (
          <div key={roomId} className={cn(styles.flexContent, styles.flexContentGap16)}>
            { rooms.length > 1 && (
              <Typography as="p" variant="body-s-bold-snug" className={cn(styles.roomName)}>
                {roomName}
              </Typography>
            )}
            <div className={cn(styles.flexContent, styles.flexContentGap12)}>
              {participantIds.map((participantId) => {
                const participant = participants?.[participantId]
                if (!participant || participant.availableOptions.length === 0) {
                  return null
                }

                return (
                  <ClothingServicePerParticipant
                    {...participant}
                    key={participantId}
                  />
                )
              })}
            </div>
          </div>
        ))}
        {isEmpty(rooms)
        && Object.values(participants ?? {}).map(participant => (
          <ClothingServicePerParticipant {...participant} key={participant.id} />
        ))}
        <ParticipantMenuSelection />
      </div>
    </CheckoutSection>
  )
}

function ParticipantMenuSelection() {
  const { service } = useService(ServiceType.Sweater)
  const [isParticipantMenuOpen, setIsParticipantMenuOpen] = React.useState(false)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { startEditingService } = useClothingServiceStore()

  const handleClick = React.useCallback((participantId: string) => {
    if (!participantId)
      return
    if (!service)
      return
    const { participants } = service

    startEditingService({
      participantId,
      serviceType: ServiceType.Sweater,
      isEditing: true,
    })

    if (isParticipantMenuOpen) {
      setIsParticipantMenuOpen(false)
    }
    sendEvent(
      createClothingServiceEvent({
        result: 'Add sweater for',
        type: participants[participantId].name,
        description: 'Sweater',
        element: 'clickable text',
      }),
    )
  }, [sendEvent, isParticipantMenuOpen, startEditingService, service])

  if (!service)
    return null

  const { participants } = service

  const participantsWithoutClothingSelected = Object.values(participants).filter(({ selectedValue }) => !selectedValue || selectedValue === service.isNoneOption)

  if (!participantsWithoutClothingSelected.length)
    return null

  function toggle() {
    setIsParticipantMenuOpen(!isParticipantMenuOpen)
  }

  return (
    <ListItem variant="outlined" className={cn(styles.participantMenuSelectionListItem)}>
      {participantsWithoutClothingSelected.length > 1 && (
        <Menu.Root open={isParticipantMenuOpen} onOpenChange={toggle}>
          <div className={styles.menuTrigger}>
            <Menu.Trigger>
              <Button size="S" variant="secondary">{intl.formatMessage({ id: 'checkout_steps_clothing_sweater_add_button_for' })}</Button>
            </Menu.Trigger>
          </div>
          <Menu.Content>
            {participantsWithoutClothingSelected.map(participant => (
              <Menu.Item key={participant.id} onClick={() => handleClick(participant.id)}>
                {intl.formatMessage({ id: 'checkout_steps_clothing_participant_menu_item' }, { name: participant.name })}
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Root>
      )}
      {participantsWithoutClothingSelected.length === 1 && (
        <Button size="S" variant="secondary" onClick={() => handleClick(participantsWithoutClothingSelected[0].id)}>
          {values(participants).length > 1 && intl.formatMessage({ id: 'checkout_steps_clothing_sweater_add_button_per_participant' }, { name: participantsWithoutClothingSelected[0].name })}
          {values(participants).length === 1 && intl.formatMessage({ id: 'checkout_steps_clothing_sweater_add_button' })}
        </Button>
      )}
    </ListItem>
  )
}

function ClothingServicePerParticipant(participant: CheckoutServiceParticipant) {
  const { selectedValue, name } = participant
  const { service } = useService(ServiceType.Sweater)
  const { sendEvent } = useAnalytics()
  const { startEditingService } = useClothingServiceStore()

  const { isDesktopSm } = useBreakpoints()

  const intl = useIntl()

  const handleClick = React.useCallback(() => {
    if (!participant)
      return
    if (!service)
      return

    startEditingService({
      participantId: participant.id,
      serviceType: ServiceType.Sweater,
      isEditing: true,
    })

    sendEvent(
      createClothingServiceEvent({
        result: 'Change',
        type: participant.name,
        description: 'Sweater',
        element: 'element clicked',
      }),
    )
  }, [sendEvent, startEditingService, service, participant])

  if (!service)
    return null

  const { options, participants } = service

  return (
    <>
      { selectedValue && selectedValue !== service.isNoneOption
      && (
        <ListItem variant="outlined" onClick={handleClick} className={cn(styles.participantRowListItem)}>
          <TitleListCell
            overline={
              Object.values(participants).length > 1 ? intl.formatMessage({ id: 'checkout_steps_clothing_for_participant' }, { name }) : undefined
            }
            title={intl.formatMessage({ id: 'checkout_steps_clothing_selected_sweater_option' }, {
              gender: intl.formatMessageIfExists({ id: `checkout_steps_clothing_sheet_genders_${options[selectedValue].gender?.toLowerCase()}` }),
              size: options[selectedValue].size,
            })}
          />
          <div className={cn(styles.itemPrice)}>
            <div className={cn(styles.priceBlock)}>
              <Typography variant="body-m-regular-tight" as="span" className={cn(styles.price)}>
                { selectedValue ? intl.formatCurrency(options[selectedValue].price, { showSign: options[selectedValue].price !== 0 }) : ''}
              </Typography>
              { !isDesktopSm && (
                <Button variant="transparentNeutral" className={cn(styles.openSheetButton)}>
                  <Icon glyph="ChevronRight" size={24} />
                </Button>
              )}
              { isDesktopSm && (
                <Button variant="secondary" onClick={handleClick} size="S">
                  {intl.formatMessage({ id: 'checkout_steps_clothing_edit_button' })}
                </Button>
              )}
            </div>
          </div>
        </ListItem>
      )}
      <ClothingServiceSheet participant={participant} />
    </>
  )
}

function ClothingServiceSheet({ participant }: { participant: CheckoutServiceParticipant }) {
  const { isTablet } = useBreakpoints()
  const clothingWrapperSide = isTablet ? 'right' : 'bottom' as SheetVariant
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { endEditingService } = useClothingServiceStore()

  const { id: participantId, name } = participant
  const { service, setServiceValue } = useService(ServiceType.Sweater)

  const handleSaveValue = React.useCallback(() => {
    if (!service)
      return
    const { id } = service.editingSelectedValues[participantId]
    endEditingService(`${ServiceType.Sweater}-${participantId}`)
    setServiceValue(saveEditingClothingServiceValue({ participantId, value: id })(service))
    sendEvent(
      createClothingServiceEvent({
        result: 'Save changes',
        type: '',
        description: 'Sweater',
        element: 'clickable text',
      }),
    )
  }, [sendEvent, participantId, service, setServiceValue, endEditingService])

  const handleCancelClick = React.useCallback(() => {
    if (!participantId)
      return
    if (!service)
      return

    endEditingService(`${ServiceType.Sweater}-${participantId}`)

    sendEvent(
      createClothingServiceEvent({
        result: `Cancel`,
        description: 'Sweater',
        element: 'clickable text',
        type: '',
      }),
    )
  }, [sendEvent, service, participantId, endEditingService])

  const isParticipantBeingEdited = useClothingServiceStore((clothingServiceIsBeingEdited(`${ServiceType.Sweater}-${participantId}`)))

  if (!service)
    return null
  const { participants } = service

  return (
    <Sheet.Root open={isParticipantBeingEdited} onOpenChange={open => !open && handleCancelClick()}>
      <Sheet.Body side={clothingWrapperSide}>
        <Sheet.Header
          titleLabel={intl.formatMessage({ id: 'checkout_steps_clothing_sweater_sheet_title' })}
          closeLabel={intl.formatMessage({ id: 'checkout_steps_clothing_sheet_cancel_button' })}
        />
        <Sheet.Content className={cn(styles.clothingContent)}>
          <div className={cn(styles.flexContent, styles.flexContentGap16)}>
            {participants && Object.values(participants).length > 1 && <Typography variant="body-l-bold-snug" className={cn(styles.sheetParticipantName)}>{intl.formatMessage({ id: 'checkout_steps_clothing_for_participant_label' }, { name })}</Typography>}
            <SelectOptionFilter participantId={participantId} />
            <GenderFilter participantId={participantId} />
            <SizeFilter participantId={participantId} />
          </div>
        </Sheet.Content>
        <Sheet.Footer>
          <Button variant="primary" onClick={handleSaveValue} fullWidth>
            {intl.formatMessage({ id: 'checkout_steps_clothing_accept_text_button' })}
          </Button>
        </Sheet.Footer>
      </Sheet.Body>
    </Sheet.Root>
  )
}

function GenderFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.Sweater)
  const { sendClothingGenderFilterClickEvent } = useClickFilterClothingService(service, 'Sweater')

  const handleGenderChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingClothingServiceGender({
        participantId,
        value,
      }),
    )

    sendClothingGenderFilterClickEvent(value)
  }, [sendClothingGenderFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null
  const { gender: genderSelected, id: selectedId } = clothingServiceEditingValueByIdSelector(service, participantId) || {}
  let genders = clothingGenderSelector(service, selectedId)
  if (genders.length === 0)
    return null

  genders = genders.filter(gender => GENDERS_ORDER.includes(gender)).sort((a, b) => GENDERS_ORDER.indexOf(a) - GENDERS_ORDER.indexOf(b))

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_clothing_sheet_gender_text' })}>
      <ToggleButtonGroup type="single" defaultValue={genderSelected} onValueChange={handleGenderChange} className={cn(styles.styledToggleGroup)}>
        {genders.map(gender => (
          <ToggleButton key={gender} value={gender} disabled={gender === genderSelected} size="M">
            <Typography>{intl.formatMessage({ id: `checkout_steps_clothing_sheet_genders_${gender?.toLowerCase()}` })}</Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </FormField>
  )
}

function SizeFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.Sweater)
  const { sendClothingSizeFilterClickEvent } = useClickFilterClothingService(service, 'Sweater')

  const handleSizeChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingClothingServiceSize({
        participantId,
        value,
      }),
    )

    sendClothingSizeFilterClickEvent(value)
  }, [sendClothingSizeFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const { size: sizeSelected, gender: genderSelected, id: selectedId } = clothingServiceEditingValueByIdSelector(service, participantId) || {}
  let sizes = clothingSizeSelector(service, genderSelected ?? '', selectedId)
  if (sizes.length === 0)
    return null

  sizes = sizes.sort((a, b) => SIZES_ORDER.indexOf(a) - SIZES_ORDER.indexOf(b))

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_clothing_sheet_sizes_text' })}>
      <ToggleButtonGroup type="single" value={sizeSelected} onValueChange={handleSizeChange} className={cn(styles.styledToggleGroup)}>
        {sizes.map(size => (
          <ToggleButton key={size} value={size} disabled={size === sizeSelected} size="M">
            <Typography>{size}</Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </FormField>
  )
}

function SelectOptionFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.Sweater)
  const { sendClothingOptionFilterClickEvent } = useClickFilterClothingService(service, 'Sweater')

  const handleValueChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingClothingServiceValue({
        participantId,
        value,
      }),
    )

    sendClothingOptionFilterClickEvent(value)
  }, [sendClothingOptionFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const { options, participants } = service
  const { id: optionSelectedId, size: sizeSelected, gender: genderSelected } = clothingServiceEditingValueByIdSelector(service, participantId) || {}

  const { availableOptions: availableOptionsParticipant } = participants[participantId]

  let allGenders = Array.from(new Set(Object.values(options).filter(option => option.gender !== NO_GENDER).map(option => option.gender)))
  allGenders = allGenders.sort((a, b) => GENDERS_ORDER.indexOf(a) - GENDERS_ORDER.indexOf(b))
  const gender = (genderSelected && genderSelected !== NO_GENDER) ? genderSelected : allGenders[0]

  let allSizes = Array.from(new Set(Object.values(options).filter(option => option.size !== NO_SIZE).map(option => option.size)))
  allSizes = allSizes.sort((a, b) => SIZES_ORDER.indexOf(a) - SIZES_ORDER.indexOf(b))
  const size = (sizeSelected && sizeSelected !== NO_SIZE) ? sizeSelected : allSizes[0]

  const availableOptionsForSelectedFilters = clothingServiceOptionsListSelector(service, gender, size)

  return (
    <FormField>
      <SelectableRadioButtonGroup value={optionSelectedId ?? undefined} onValueChange={handleValueChange}>
        <div className={cn(styles.flexContent, styles.flexContentGap8)}>
          {availableOptionsForSelectedFilters
            .filter(optionId => availableOptionsParticipant.includes(optionId))
            .map((optionId) => {
              const { id, badge, price } = options[optionId]

              return (
                <SelectableListItem value={id} className={genericStyles.itemOptionWrapper} key={optionId}>
                  <div className={genericStyles.itemOption}>
                    <div className={genericStyles.itemOptionControl}>
                      <RadioButton value={id} />
                    </div>
                    <div className={genericStyles.itemOptionContent}>
                      {badge && <Tag variant="success-tinted" size="M" className={cn(genericStyles.itemOptionTag)}>{badge}</Tag>}
                      <Typography variant="body-m-regular-tight" className={cn(styles.optionalValue)}>
                        {(optionId === service.isNoneOption)
                          ? intl.formatMessage({ id: 'checkout_steps_clothing_sheet_no_sweater_option' })
                          : intl.formatMessage({ id: 'checkout_steps_clothing_sheet_sweater_option' })}
                      </Typography>
                    </div>
                    <div className={genericStyles.itemOptionTrailing}>
                      <Typography variant="body-m-regular-tight" as="span" className={cn(styles.price)}>
                        {(optionId === service.isNoneOption) ? '' : intl.formatCurrency(price, { showSign: price !== 0 })}
                      </Typography>
                    </div>
                  </div>
                </SelectableListItem>
              )
            })}
        </div>
      </SelectableRadioButtonGroup>
    </FormField>
  )
}
